import React from "react";

// Option 2 weather icons: navy line icons with small gold/red accent pops.
// Consistent 2px stroke, render identically on every device.

const base = { fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", stroke: "#0B1626" };

export function WindIcon({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" {...base}>
      <path d="M3 11h13a3 3 0 1 0-3-3" />
      <path d="M3 16h19a3 3 0 1 1-3 3" />
      <path d="M3 21h10a2.5 2.5 0 1 1-2.5 2.5" />
    </svg>
  );
}

export function CompassIcon({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" {...base}>
      <circle cx="16" cy="16" r="12" />
      <path d="M16 16l6-6-3 9-9 3z" fill="#B8860B" stroke="none" />
      <circle cx="16" cy="16" r="1.5" fill="#0B1626" stroke="none" />
    </svg>
  );
}

export function ThermometerIcon({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" {...base}>
      <path d="M13 18V7a3 3 0 0 1 6 0v11a5 5 0 1 1-6 0z" />
      <circle cx="16" cy="22" r="2.5" fill="#E04A4A" stroke="none" />
    </svg>
  );
}

// Weather condition icon by Open-Meteo code, in the same line style.
export function ConditionIcon({ code = 0, size = 30 }) {
  const c = Number(code);
  if (c === 0) {
    // sun
    return (
      <svg width={size} height={size} viewBox="0 0 32 32" {...base} stroke="#B8860B">
        <circle cx="16" cy="16" r="6" />
        <path d="M16 3v3M16 26v3M3 16h3M26 16h3M6.5 6.5l2 2M23.5 23.5l2 2M25.5 6.5l-2 2M8.5 23.5l-2 2" />
      </svg>
    );
  }
  if (c <= 2) {
    // sun + small cloud
    return (
      <svg width={size} height={size} viewBox="0 0 32 32" {...base}>
        <circle cx="12" cy="12" r="4.5" stroke="#B8860B" />
        <path d="M12 4v2M4 12h2M6.5 6.5l1.5 1.5M17.5 6.5l-1.5 1.5" stroke="#B8860B" />
        <path d="M12 24h11a4 4 0 0 0 0-8 5.5 5.5 0 0 0-10.5 1.2A3.2 3.2 0 0 0 12 24z" />
      </svg>
    );
  }
  if (c === 3 || c <= 48) {
    // cloud / fog
    return (
      <svg width={size} height={size} viewBox="0 0 32 32" {...base}>
        <path d="M9 21h13a5 5 0 0 0 0-10 7 7 0 0 0-13.5 2A4 4 0 0 0 9 21z" />
        {c > 3 && <path d="M7 25h12M9 28h9" stroke="#5A6A7A" />}
      </svg>
    );
  }
  if (c <= 67 || (c >= 80 && c <= 82)) {
    // rain
    return (
      <svg width={size} height={size} viewBox="0 0 32 32" {...base}>
        <path d="M9 18h13a5 5 0 0 0 0-10 7 7 0 0 0-13.5 2A4 4 0 0 0 9 18z" />
        <path d="M11 23l-1.5 3M17 23l-1.5 3M23 23l-1.5 3" stroke="#5B8FCF" />
      </svg>
    );
  }
  if (c <= 77) {
    // snow
    return (
      <svg width={size} height={size} viewBox="0 0 32 32" {...base}>
        <path d="M9 18h13a5 5 0 0 0 0-10 7 7 0 0 0-13.5 2A4 4 0 0 0 9 18z" />
        <path d="M12 24v3M18 24v3M22 24v3" stroke="#5B8FCF" />
      </svg>
    );
  }
  // storms
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" {...base}>
      <path d="M9 17h13a5 5 0 0 0 0-10 7 7 0 0 0-13.5 2A4 4 0 0 0 9 17z" />
      <path d="M15 19l-3 5h4l-2 4" stroke="#E04A4A" />
    </svg>
  );
}

// AI sparkle icon — for "Write with AI" features. Gold by default.
export function AiSparkle({ size = 16, color = "#B8860B" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M12 3l1.8 4.9L18.7 9.7l-4.9 1.8L12 16.4l-1.8-4.9L5.3 9.7l4.9-1.8z" fill={color} stroke="none" />
      <path d="M19 14l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7z" fill={color} stroke="none" />
      <path d="M6 15l.5 1.4 1.4.5-1.4.5L6 19.3l-.5-1.4-1.4-.5 1.4-.5z" fill={color} stroke="none" />
    </svg>
  );
}

// Speedometer — for SOG (speed over ground). Gold needle pop.
export function SpeedIcon({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" {...base}>
      <path d="M5 23a11 11 0 1 1 22 0" />
      <path d="M16 18l6-5" stroke="#B8860B" />
      <circle cx="16" cy="18" r="1.8" fill="#0B1626" stroke="none" />
    </svg>
  );
}

// Ruler — for race distance.
export function RulerIcon({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" {...base}>
      <rect x="4" y="11" width="24" height="10" rx="2" />
      <path d="M10 11v4M16 11v5M22 11v4" />
    </svg>
  );
}

// Target — for polar performance %. Gold center.
export function TargetIcon({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" {...base}>
      <circle cx="16" cy="16" r="11" />
      <circle cx="16" cy="16" r="6" />
      <circle cx="16" cy="16" r="2" fill="#B8860B" stroke="none" />
    </svg>
  );
}

